import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import Card from "./Card";
import Button from "./Buttons";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useFusion } from "../contexts/FeatureFlagsContext";

// Fusion F1c: map each directory department to a business unit (Business Units page).
// Gate mirrors the endpoint exactly: Fusion flag on AND canManageOrganizationRisks (RISK_MANAGER+ at
// ORG). GET / PUT /BusinessUnits/department-map; the PUT replaces the whole stored map.

const NOT_MAPPED = "";
// Select value for a row whose stored business unit no longer exists. The API rejects unknown ids,
// so Save stays disabled until the user picks a business unit or "Not mapped".
const STALE = "__stale__";

const initialSelections = (data) => {
  const ids = new Set((data?.businessUnits || []).map((bu) => bu.id));
  const out = {};
  for (const row of data?.departments || []) {
    const mapped = row.mappedBusinessUnitId;
    if (mapped == null) out[row.departmentKey] = NOT_MAPPED;
    else out[row.departmentKey] = ids.has(mapped) ? String(mapped) : STALE;
  }
  return out;
};

const errorMessage = (error, fallback) => {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join("\n");
  return error?.response?.data?.message || fallback;
};

const DepartmentBusinessUnitMap = ({ organizationId }) => {
  const apiCall = useApi();
  const fusionEnabled = useFusion();
  const { canManageOrganizationRisks } = useRBAC();

  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [initial, setInitial] = useState({});
  const [selections, setSelections] = useState({});

  if (!fusionEnabled || !canManageOrganizationRisks(organizationId)) return null;

  const applyResponse = (response) => {
    const next = {
      departments: response?.departments || [],
      businessUnits: response?.businessUnits || [],
    };
    const sel = initialSelections(next);
    setData(next);
    setInitial(sel);
    setSelections(sel);
  };

  const load = async () => {
    setLoading(true);
    try {
      applyResponse(await apiCall("GET", "/BusinessUnits/department-map"));
      setLoaded(true);
    } catch (error) {
      alert(errorMessage(error, "Error loading department mapping."));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded && !loading) load();
  };

  const departments = data?.departments || [];
  const businessUnits = data?.businessUnits || [];
  const buName = (id) => businessUnits.find((bu) => bu.id === id)?.name;

  const changed = departments.some((row) => selections[row.departmentKey] !== initial[row.departmentKey]);
  const hasStale = departments.some((row) => selections[row.departmentKey] === STALE);
  const canSave = changed && !hasStale && !saving;

  const setSelection = (key, value) => setSelections((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    // The endpoint replaces the stored map: send every row that holds a business unit, not only
    // the changed ones. "Not mapped" rows are left out, which removes their mapping.
    const body = departments
      .filter((row) => selections[row.departmentKey] && selections[row.departmentKey] !== STALE)
      .map((row) => ({ department: row.label, businessUnitId: Number(selections[row.departmentKey]) }));
    setSaving(true);
    try {
      applyResponse(await apiCall("PUT", "/BusinessUnits/department-map", body));
    } catch (error) {
      alert(errorMessage(error, "Error saving department mapping."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-8">
      <button
        type="button"
        className="w-full flex items-start text-left"
        onClick={toggle}
        aria-expanded={expanded}
      >
        {expanded
          ? <ChevronDown className="h-5 w-5 mt-1 mr-2 text-gray-500" />
          : <ChevronRight className="h-5 w-5 mt-1 mr-2 text-gray-500" />}
        <div>
          <h2 className="text-xl font-semibold">Department mapping</h2>
          <p className="text-sm text-gray-600">
            Link the departments in your identity directory to business units. Fusion uses this to show which business unit an identity belongs to.
          </p>
        </div>
      </button>

      {expanded && (
        <div className="mt-6">
          {loading && !loaded ? (
            <p className="text-sm text-gray-500">Loading departments...</p>
          ) : !loaded ? null : departments.length === 0 ? (
            <p className="text-sm text-gray-500">
              No departments found yet. Departments appear after your identity directory syncs.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identities</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {departments.map((row) => {
                      const value = selections[row.departmentKey] ?? NOT_MAPPED;
                      const isStale = value === STALE;
                      const suggestion = row.suggestedBusinessUnitId != null ? buName(row.suggestedBusinessUnitId) : undefined;
                      return (
                        <tr key={row.departmentKey}>
                          <td className="px-4 py-2 text-sm text-content-primary">{row.label}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{row.identityCount}</td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                aria-label={`Business unit for ${row.label}`}
                                className={`border rounded-md px-2 py-1 text-sm bg-theme-card-solid ${isStale ? "border-amber-500" : "border-gray-300"}`}
                                value={value}
                                onChange={(e) => setSelection(row.departmentKey, e.target.value)}
                              >
                                {isStale && (
                                  <option value={STALE} disabled>Business unit no longer exists</option>
                                )}
                                <option value={NOT_MAPPED}>Not mapped</option>
                                {businessUnits.map((bu) => (
                                  <option key={bu.id} value={String(bu.id)}>{bu.name}</option>
                                ))}
                              </select>
                              {value === NOT_MAPPED && suggestion && (
                                <span className="inline-flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  Suggested: {suggestion}
                                  <button
                                    type="button"
                                    className="font-medium underline"
                                    onClick={() => setSelection(row.departmentKey, String(row.suggestedBusinessUnitId))}
                                  >
                                    Apply
                                  </button>
                                </span>
                              )}
                            </div>
                            {isStale && (
                              <p className="mt-1 flex items-center text-xs text-amber-700">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                The mapped business unit was deleted. Choose a business unit or "Not mapped" before saving.
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end">
                <Button size="sm" onClick={save} disabled={!canSave}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default DepartmentBusinessUnitMap;
